<template>
  <Modal
    v-model="openComputed"
    :max-width="700"
    :fullscreen="$vuetify?.display?.xs"
    :loading="loading"
  >
    <template v-slot:activator="{ props }">
      <v-btn v-bind="props">{{ t('create-post') }}</v-btn>
    </template>
    <template v-slot:title>
      {{ selectedPost ? t('update') : t('create') }} {{ t('post') }}
    </template>
    <template v-slot:content>
      <v-text-field
        v-model="postTitle"
        :label="t('title')"
        variant="outlined"
        density="compact"
        hide-details
        :rules="[(v) => !!v || 'Required']"
        class="mb-4"
      />
      <v-card variant="outlined">
        <div class="tabs-container">
          <v-tabs v-model="preview">
            <v-tab :value="false">{{ t('write') }}</v-tab>
            <v-tab :value="true">{{ t('preview') }}</v-tab>
          </v-tabs>
          <div v-if="!preview">
            <v-btn
              variant="text"
              icon="mdi-format-header-pound"
              @click="addHeading"
            />
            <v-btn variant="text" icon="mdi-format-bold" @click="addBold" />
            <v-btn variant="text" icon="mdi-format-italic" @click="addItalic" />
            <v-btn variant="text" icon="mdi-link" @click="addLink" />
            <v-menu :close-on-content-click="false" v-model="addingImage">
              <template v-slot:activator="{ props }">
                <v-btn
                  variant="text"
                  icon="mdi-image"
                  v-bind="props"
                />
              </template>
              <v-card style="min-width: 300px;">
                <v-card-text class="d-flex flex-column ga-4">
                  <v-file-input
                    v-model="imageFile"
                    label="Select Image"
                    variant="outlined"
                    density="compact"
                    hide-details
                    accept="image/*"
                  />
                  <v-text-field
                    v-model="imageAltText"
                    label="Alt Text"
                    variant="outlined"
                    density="compact"
                    hide-details
                  />
                  <v-text-field
                    v-model="imageUrl"
                    label="Image URL"
                    variant="outlined"
                    density="compact"
                    hide-details
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn-primary @click="addImage" :disabled="!imageFile">
                    Insert Image
                  </v-btn-primary>
                </v-card-actions>
              </v-card>
            </v-menu>
            <v-btn variant="text" icon="mdi-keyboard-tab" @click="addTab" />
            <v-btn
              variant="text"
              icon="mdi-format-list-bulleted"
              @click="addUnorderedList"
            />
            <v-btn
              variant="text"
              icon="mdi-format-list-numbered"
              @click="addOrderedList"
            />
          </div>
        </div>
        <div
          v-show="preview"
          v-html="renderedMarkdown"
          class="markdown-preview"
          ref="previewArea"
        ></div>
        <v-textarea
          v-show="!preview"
          v-model="postText"
          autofocus
          class="markdown-input"
          ref="inputArea"
          hide-details
          :rules="[(v) => !!v || 'Required']"
          @keypress.enter="enterPressed"
        />
      </v-card>
      <v-text-field
        v-model="releaseDate"
        label="Release Date"
        type="datetime-local"
        variant="outlined"
        density="compact"
        hide-details
        class="mt-4"
      />
    </template>
    <template v-slot:actions>
      <v-spacer />
      <v-btn
        v-if="!selectedPost"
        color="primary"
        @click="createPost"
        :disabled="!isValid || loading"
      >
        {{ t('post') }}
      </v-btn>
      <template v-else>
        <v-btn
          color="error"
          @click="deletePost"
          :disabled="!isValid || loading"
        >
          {{ t('delete') }}
        </v-btn>
        <v-btn
          color="primary"
          @click="updatePost"
          :disabled="!isValid || loading"
        >
          {{ t('update') }}
        </v-btn>
      </template>
    </template>
  </Modal>
</template>
<script setup>
import { ref, toRefs, computed, watch, nextTick } from 'vue';
import { useRootStore } from '../../../stores/root';
import { storeToRefs } from 'pinia';
import { postAPI, putAPI, deleteAPI } from './whatsnewapi';
import Modal from '../../Modal.vue';
import { md } from './markdown';
import { useI18n } from 'vue-i18n';
import { fa } from 'vuetify/locale';
const { t } = useI18n();

const emit = defineEmits(['refresh']);
const props = defineProps({
  selectedPost: Object,
});
const { selectedPost } = toRefs(props);

const loading = ref(false);
const postTitle = ref('');
const postText = ref('');
const preview = ref(false);

// function that gets YYYY-MM-DDTHH:mm format
function getDateTime(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

const releaseDate = ref(getDateTime(new Date()));

const isValid = computed(() => postTitle.value && postText.value);

const open = ref(false);
const openComputed = computed({
  get: () => open.value,
  set: (val) => {
    open.value = val;
    if (!val) {
      postTitle.value = '';
      postText.value = '';
      emit('refresh');
    }
  },
});

watch(selectedPost, (newVal) => {
  if (newVal) {
    postTitle.value = newVal.title;
    postText.value = newVal.content;
    releaseDate.value = getDateTime(new Date(newVal.releaseDate));
    openComputed.value = true;
  }
});

const renderedMarkdown = computed(() => {
  return md.render(postText.value || '');
});

const store = useRootStore();
const { locations, orgId, enterpriseClient } = storeToRefs(store);

async function createPost() {
  loading.value = true;
  try {
    await postAPI({
      body: {
        title: postTitle.value,
        content: postText.value,
        enterprise: enterpriseClient.value,
        releaseDate: new Date(releaseDate.value).toISOString(),
      },
    });
    open.value = false;
    postTitle.value = '';
    postText.value = '';
    emit('refresh');
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}

async function updatePost() {
  loading.value = true;
  try {
    await putAPI({
      body: {
        ...selectedPost.value,
        title: postTitle.value,
        content: postText.value,
        releaseDate: new Date(releaseDate.value).toISOString(),
      }
    });
    open.value = false;
    postTitle.value = '';
    postText.value = '';
    emit('refresh');
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}

async function deletePost() {
  loading.value = true;
  try {
    await deleteAPI({
      body: selectedPost.value,
    });
    open.value = false;
    postTitle.value = '';
    postText.value = '';
    emit('refresh');
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}

const inputArea = ref();
async function addHeading() {
  const start = inputArea.value.selectionStart;
  const end = inputArea.value.selectionEnd;
  const text = inputArea.value.value;
  const newText = `${text.slice(0, start)}# ${text.slice(start, end)}${text.slice(end)}`;
  postText.value = newText;
  await nextTick();
  inputArea.value.focus();
  inputArea.value.setSelectionRange(start + 2, end + 2);
}

async function addBold() {
  const start = inputArea.value.selectionStart;
  const end = inputArea.value.selectionEnd;
  const text = inputArea.value.value;
  const newText = `${text.slice(0, start)}**${text.slice(start, end)}**${text.slice(end)}`;
  postText.value = newText;
  await nextTick();
  inputArea.value.focus();
  inputArea.value.setSelectionRange(start + 2, end + 2);
}

async function addItalic() {
  const start = inputArea.value.selectionStart;
  const end = inputArea.value.selectionEnd;
  const text = inputArea.value.value;
  const newText = `${text.slice(0, start)}_${text.slice(start, end)}_${text.slice(end)}`;
  postText.value = newText;
  await nextTick();
  inputArea.value.focus();
  inputArea.value.setSelectionRange(start + 1, end + 1);
}

async function addLink() {
  const start = inputArea.value.selectionStart;
  const end = inputArea.value.selectionEnd;
  const text = inputArea.value.value;
  const newText = `${text.slice(0, start)}[${text.slice(start, end)}](url)${text.slice(end)}`;
  postText.value = newText;
  await nextTick();
  inputArea.value.focus();
  inputArea.value.setSelectionRange(start + 1, end + 1);
}

// videos have custom parsers. @[video](https://vimeo.com/831229281)
// @[video](url)
// video is always the same, but the url is different
// async function addVideo() {
//   const start = inputArea.value.selectionStart;
//   const end = inputArea.value.selectionEnd;
//   const text = inputArea.value.value;
//   const newText = `@[video](${text.slice(start, end)})`;
//   postText.value = newText;
//   await nextTick();
//   inputArea.value.focus();
//   inputArea.value.setSelectionRange(start + 9, end + 9);
// }


const imageFile = ref();
const imageAltText = ref('');
const imageUrl = ref('');
const addingImage = ref(false);
function convertImageToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
}
// [![Alt text](https://example.com/image.png)](https://example.com)
async function addImage() {
  const start = inputArea.value.selectionStart;
  const base64 = await convertImageToBase64(imageFile.value);
  let newText = '';
  if (imageUrl.value) {
    newText = `[![${imageAltText.value}](${base64})](${imageUrl.value})`;
  } else {
    newText = `![${imageAltText.value}](${base64})`;
  }
  postText.value = newText;
  await nextTick();
  inputArea.value.focus();
  inputArea.value.setSelectionRange(
    start + newText.length,
    start + newText.length,
  );
  imageFile.value = null;
  imageAltText.value = '';
  imageUrl.value = '';
  addingImage.value = false;
}

async function addUnorderedList() {
  const start = inputArea.value.selectionStart;
  const end = inputArea.value.selectionEnd;
  const text = inputArea.value.value;
  const newText = `${text.slice(0, start)}- ${text.slice(start, end)}${text.slice(end)}`;
  postText.value = newText;
  await nextTick();
  inputArea.value.focus();
  inputArea.value.setSelectionRange(start + 2, end + 2);
}

async function addOrderedList() {
  const start = inputArea.value.selectionStart;
  const end = inputArea.value.selectionEnd;
  const text = inputArea.value.value;
  const newText = `${text.slice(0, start)}1. ${text.slice(start, end)}${text.slice(end)}`;
  postText.value = newText;
  await nextTick();
  inputArea.value.focus();
  inputArea.value.setSelectionRange(start + 3, end + 3);
}

async function addTab() {
  // just add &nbsp; to the start of the input
  const start = inputArea.value.selectionStart;
  const end = inputArea.value.selectionEnd;
  const text = inputArea.value.value;
  const newText = `${text.slice(0, start)}&nbsp;${text.slice(start, end)}${text.slice(end)}`;
  postText.value = newText;
  inputArea.value.focus();
  await nextTick();
  inputArea.value.setSelectionRange(start + 6, end + 6);
}

function enterPressed(event) {
  // if this line is an ordered or unordered list, add a new line with the same list type
  const text = inputArea.value.value;
  const lines = text.split('\n');
  const currentLine = lines[lines.length - 1];
  if (currentLine.match(/^- /)) {
    event.preventDefault();
    // if the only thing in that line is the bullet, remove it
    if (currentLine === '- ') {
      postText.value = postText.value.slice(0, -2);
    } else {
      postText.value += '\n- ';
    }
    // postText.value += "\n- ";
  } else if (currentLine.match(/^\d+\. /)) {
    event.preventDefault();
    // if the only thing in that line is the number, remove it
    if (currentLine.match(/^\d+\. $/)) {
      postText.value = postText.value.slice(0, -3);
    } else {
      // find the number of the last line
      const lastNumber = Number(currentLine.match(/^(\d+)\. /)[1]);
      postText.value += `\n${lastNumber + 1}. `;
    }
  }
}
</script>
<i18n>
{
  "en": {
    "create-post": "Create Post",
    "update": "Update",
    "create": "Create",
    "post": "Post",
    "title": "Title",
    "write": "Write",
    "preview": "Preview",
    "delete": "Delete"
  },
  "es": {
    "create-post": "Crear Publicación",
    "update": "Actualizar",
    "create": "Crear",
    "post": "Publicación",
    "title": "Titulo",
    "write": "Escribir",
    "preview": "Vista Previa",
    "delete": "Borrar"
  }
}
</i18n>
<style scoped>
.tabs-container {
  display: flex;
  gap: 16px;
  justify-content: space-between;
}
.markdown-preview {
  padding: 16px;
  overflow-y: auto;
  max-height: 400px;
}
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

:deep(ul),
:deep(ol) {
  padding-inline-start: 40px;
}
:deep(img) {
  max-width: 100%;
}
</style>
